<template lang="pug">
.judgement-waiter-tab(v-cloak)
  .mb-3
  .judgement-input(v-if="_isMineFacility")
    .message-container(v-if="!existJudgementTemplateId")
      .warning-message
        div 入所判定様式が設定されていないため、更新できません。
        div 設定をご確認ください。
    .template-container(v-if="template && existJudgementTemplateId")
      .information.mb-2(v-if="information" style="white-space: pre-wrap; word-wrap: break-word;") {{ information }}

      .template(v-for="(property, index) in template.properties")
        WaiterJudgeTemplateInputItem(v-model="template.properties[index]" :isChaild="false")
    .mb-3
    .d-flex.flex-row.justify-content-end.mr-3.mb-2.ml-3(v-if="existJudgementTemplateId")
      .switch.mr-3
        WaiterJudgementSwitch(v-model="judged")

      span.point.mr-2 合計
      span.point.mr-2 {{ point }}
      span.point 点

  .register-footer(:class="{ 'box-footer': _isMineFacility }")
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.other-facilities-judged-btn(@click="onOtherFacilitiesJudged" type="button"
        :disabled="otherFacilitiesJudgedOptions <= 0" :class="{ disable: (otherFacilitiesJudgedOptions <= 0) ? true : false }") 他施設の判定結果
      button.btn.update-btn(v-if="existJudgementTemplateId && _isMineFacility" @click="onUpdate" type="button") 更新

  JudgementUpdateConfirmModal(v-model="showUpdateModal" :targetFacilities="concurrentRegisterFacilities" @ok="updateWaiter")

  OtherFacilitiesJudgedResultModal(v-model="showOtherFacilitiesJudgedModal" :options="otherFacilitiesJudgedOptions" :waiters="waitersInSystem")
  
  RelativeLoading(:isBusy="isRelativeLodingBusy" :message="relativeLodingBusyMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import LoginUser from "@/store/LoginUser";

import TemplatesService, {
  TemplateItem,
  TemplateItemText,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemSection,
  TemplateType,
  TemplateItemMultipleCheckbox,
  AdmissionJudgementTemplatesGetModel,
  CalcType,
  Template,
  TemplateItemInput
} from "@/commons/services/AdmissionJudgementTemplates";
import { TemplateItem as ApplicationTemplateItem, TemplateItemInput as ApplicationTemplateItemInput } from "@/commons/services/AdmissionApplicationTemplates";
import WaitersService, { AdmissionApplication, UpdateType, WaitersGetModel, WaitersPatchModel } from "@/commons/services/Waiters";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import WaiterJudgeTemplateInputItem from "@/pc/containers/waiters/WaiterJudgeTemplateInputItem.vue";
import WaiterJudgementSwitch from "@/pc/basics/WaiterJudgementSwitch.vue";
import OtherFacilitiesJudgedResultModal from "@/pc/containers/waiters/OtherFacilitiesjudgedResultModal.vue";
import JudgementUpdateConfirmModal, { TargetFacility } from "@/pc/containers/waiters/JudgementUpdateConfirmModal.vue";
import { ResidentStatus } from "@/commons/services/Residents";

/**
 * ### 入所評価判定タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    WaiterJudgeTemplateInputItem,
    WaiterJudgementSwitch,
    OtherFacilitiesJudgedResultModal,
    JudgementUpdateConfirmModal
  },
  $_veeValidate: { validator: "new" }
})
export default class JudgementWaiterTab extends Vue {
  /** 入所申込テンプレートサービス */
  @Inject("AdmissionJudgementTemplatesService") templatesService!: TemplatesService;
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** タブ全体ローディング表示可否 */
  isRelativeLodingBusy: boolean = false;
  /** タブ全体ローディングメッセージ */
  relativeLodingBusyMessage: string = "";

  /** 入居申込テンプレート情報が設定されているか否か */
  existJudgementTemplateId: boolean = true;

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 判定結果を同時登録する他施設の選択情報 */
  concurrentRegisterFacilities: Array<TargetFacility> = [];

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 他施設の判定結果モーダル表示可否 */
  showOtherFacilitiesJudgedModal: boolean = false;
  /** 他施設の判定結果モーダルで使用する選択肢 */
  otherFacilitiesJudgedOptions: Array<{ value: string; text: string }> = [];
  /** 他施設の判定結果モーダルで表示する待機者 */
  waitersInSystem: Array<any> = [];

  /** 判定済みか否か */
  judged: boolean = false;
  /** 判定対象の待機者 */
  waiter: WaitersGetModel | null = null;
  /** 施設全体で指定している待機者判定テンプレート */
  sourceTemplate: AdmissionJudgementTemplatesGetModel | null = null;
  /** テンプレート説明文 */
  information: string | undefined = "";
  /** 画面表示する判定テンプレート */
  template: Template | null = null;
  /** 点数 */
  point: number = 0;

  @Watch("template", {
    deep: true
  })
  public watchTemplate(newValue: Template): void {
    let totalPoint: number = 0;
    let replacePoint: number | null = null;
    let points: { totalPoint: number; replacePoint: number | null } = { totalPoint, replacePoint };
    this.calculateReplacePoint(newValue.properties, points);
    this.calculateTotalPoint(newValue.properties, points);

    if (points.replacePoint != null) {
      this.point = points.replacePoint;
    } else {
      this.point = points.totalPoint;
    }

    return;
  }

  /**
   * ### 「置き換え」の点数計算を行う
   *
   * - 再帰呼び出しして入所判定テンプレートの内容を検索する
   * - 計算種別が「置き換え」のものが存在した場合、置き換え後の点数を引数のreplacePointに格納する
   * - TODO 置き換えが複数存在した場合は何を優先するのか決まっていないので検討が必要かもしれない
   *   - 今は最後に出てきたプロパティで上書きされる
   */
  public calculateReplacePoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateReplacePoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateReplacePoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: TemplateItemText = property as TemplateItemText;
          if (textItem.answer == null) {
            continue;
          }
          if (textItem.calcType == CalcType.REPLACE) {
            points.replacePoint = Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: TemplateItemRadio = property as TemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }
          if (radioItem.calcType == CalcType.REPLACE) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.replacePoint = option.point ? Number(option.point) : (option.point as null);
                break;
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: TemplateItemMultipleCheckbox = property as TemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.REPLACE) {
            let point: number | null = null;
            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }
                if (point == null || point < Number(multipleCheckboxItem.points[i])) {
                  point = Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            points.replacePoint = point != null ? point : null;
          }
        }
      }
    }
    return;
  }

  /**
   * ### 「加算」の点数計算を行う
   *
   * - 再帰呼び出しして入所判定テンプレートの内容を検索する
   * - 計算種別が「加算」のものが存在した場合、点数をtotalPointに加算する
   */
  public calculateTotalPoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateTotalPoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateTotalPoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: TemplateItemText = property as TemplateItemText;
          if (textItem.answer == null) {
            continue;
          }
          if (textItem.calcType == CalcType.ADD || textItem.calcType == CalcType.CALC_ADD) {
            points.totalPoint += Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: TemplateItemRadio = property as TemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }
          if (radioItem.calcType == CalcType.ADD || radioItem.calcType == CalcType.CALC_ADD) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.totalPoint += Number(option.point);
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: TemplateItemMultipleCheckbox = property as TemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.ADD || multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
            let point: number = 0;

            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }

                // 計算種別：加算の場合は選択肢で最高得点を採用する
                if (multipleCheckboxItem.calcType == CalcType.ADD) {
                  if (point < Number(multipleCheckboxItem.points[i])) {
                    point = Number(multipleCheckboxItem.points[i]);
                  }
                }
                // 計算種別：計算加算の場合は選択されたpointを合算する
                else if (multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
                  point += Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            // 計算種別：計算加算の場合は上限までの点数とする
            if (multipleCheckboxItem.calcType == CalcType.CALC_ADD && multipleCheckboxItem.limitPoint != null && multipleCheckboxItem.limitPoint != undefined) {
              if (multipleCheckboxItem.limitPoint < point) {
                point = multipleCheckboxItem.limitPoint;
              }
            }

            points.totalPoint += point;
          }
        }
      }
    }
    return;
  }

  /**
   * ### 画面情報をリフレッシュする
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### vue lifecycle created
   *
   * - 入居申込テンプレートが設定されていない場合はメッセージを表示して終了する
   * - 上記以外は画面表示情報を作成する
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 判定対象の待機者を取得する
   * - 入居判定で使用する項目を決定する
   *   - 未判定の場合、入居判定テンプレートを取得して使用する
   *   - 判定済みの場合、待機者情報に含まれる前回入力した情報を使用する
   * - 待機者の他施設での判定結果モーダルの情報を作成する
   *   - 待機者の他施設での判定結果を取得する
   *   - 待機者の選択肢を使用する
   * - 入居判定をする際、他施設にも判定結果を登録する際に使用する選択肢を作成する
   */
  public async createView(): Promise<void> {
    if (userStore.facility.useAdmissionJudgementTemplateId == null) {
      this.existJudgementTemplateId = false;
      return;
    }

    this.isRelativeLodingBusy = true;
    this.relativeLodingBusyMessage = "読込み中...";

    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    const idinsystem: number = Number(this.$route.params.idinsystem);
    const waiter: WaitersGetModel = await this.getWaiter(facilityId, residentId, idinsystem);
    this.waiter = waiter;

    const template: AdmissionJudgementTemplatesGetModel = await this.getTemplate();
    this.sourceTemplate = template;

    if (waiter.waiter.admissionJudgement != null) {
      this.information = waiter.waiter.admissionJudgement.information;
      this.template = waiter.waiter.admissionJudgement.templateAnswer;
      this.judged = waiter.waiter.admissionJudgement.judged;
    } else {
      this.information = template.information;
      this.template = template.template;
      this.initializeTemplate(this.template.properties, waiter.waiter.admissionApplication);
    }

    // 既存バグにより、チェックボックスのanswerの値が質問数とかい離してしまった
    // 対応として、質問数と合わない場合はfalseで初期化して画面表示させる
    this.initializeCheckboxAnswer(this.template.properties);

    const residentIdInSystem: number = waiter.idInSystem;
    const waitersInSystem: Array<WaitersGetModel> = await this.getWaitersInSystem(residentIdInSystem);
    this.waitersInSystem = waitersInSystem;
    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const otherFacilitiesJudgedOptions: Array<{ value: string; text: string }> = this.createOtherFacilitiesJudgedOptions(waiterViewableFacilities, waitersInSystem);
    this.otherFacilitiesJudgedOptions = otherFacilitiesJudgedOptions;

    const concurrentRegisterFacilities: Array<TargetFacility> = this.createConcurrentRegisterFacilities(waiterViewableFacilities, waitersInSystem);
    this.concurrentRegisterFacilities = concurrentRegisterFacilities;

    this.isRelativeLodingBusy = false;

    return;
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiter(facilityId: number, residentId: number, idinsystem: number): Promise<WaitersGetModel> {
    const waitersByIdInSystem: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, idinsystem);
    for (const waiter of waitersByIdInSystem) {
      if (waiter.facilityId == facilityId) {
        return waiter;
      }
    }
    // 見つからない場合は個別取得する
    const waiter: WaitersGetModel = await this.waitersService.get(userStore, facilityId, residentId);
    return waiter;
  }

  /**
   * ### 入居判定テンプレートを取得する
   */
  public async getTemplate(): Promise<AdmissionJudgementTemplatesGetModel> {
    const response: AdmissionJudgementTemplatesGetModel = await this.templatesService.get(userStore);
    return response;
  }

  /**
   * ### 入所判定テンプレートに初期値を格納する
   */
  public initializeTemplate(properties: TemplateItem[], admissionApplication: AdmissionApplication): void {
    for (const item of properties) {
      let referenceId: string = "";
      switch (item.type) {
        case TemplateType.TEXT:
        case TemplateType.RADIO:
        case TemplateType.MULTIPLE_CHECKBOX: {
          const templateItemInput: TemplateItemInput = item as TemplateItemInput;
          referenceId = templateItemInput.referenceId;
          break;
        }
        case TemplateType.SECTION: {
          const sectionItem: TemplateItemSection = item as TemplateItemSection;
          this.initializeTemplate(sectionItem.properties, admissionApplication);
          break;
        }
        case TemplateType.RADIO_GROUP: {
          const radioGroupItem: TemplateItemRadioGroup = item as TemplateItemRadioGroup;
          for (const childItem of radioGroupItem.properties) {
            if (childItem && Array.isArray(childItem) == true) {
              this.initializeTemplate(childItem.properties, admissionApplication);
            }
          }
          break;
        }
        default: {
          referenceId = "";
          break;
        }
      }

      if (referenceId) {
        const referenceItem: ApplicationTemplateItemInput | null = this.serachTemplateProperty(admissionApplication.templateAnswer.properties, referenceId);

        console.debug("referenceId:", referenceId);
        console.debug("referenceItem:", referenceItem);

        if (referenceItem != null) {
          const _referenceItem: any = referenceItem as any;
          switch (item.type) {
            case TemplateType.TEXT: {
              const templateItemText: TemplateItemText = item as TemplateItemText;
              templateItemText.answer = _referenceItem.answer;
              break;
            }
            case TemplateType.RADIO: {
              const templateItemRadio: TemplateItemRadio = item as TemplateItemRadio;
              templateItemRadio.answer = _referenceItem.answer;
              break;
            }
            default: {
              referenceId = "";
              break;
            }
          }
        }
      }
    }

    return;
  }

  /**
   * ### テンプレートのpropertiesから指定されたidの要素を返す
   */
  public serachTemplateProperty(properties: Array<ApplicationTemplateItem>, idName: string): ApplicationTemplateItemInput | null {
    for (const property of properties) {
      // 配下にperopetiesがあれば再帰検索
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const section: TemplateItemSection = property as TemplateItemSection;
          const item: ApplicationTemplateItemInput | null = this.serachTemplateProperty(section.properties, idName);
          if (item != null) {
            return item;
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const radioGroup: TemplateItemRadioGroup = property as TemplateItemRadioGroup;
          if (radioGroup.properties != null) {
            for (const childProperties of radioGroup.properties) {
              if (childProperties.properties != null) {
                const item: ApplicationTemplateItemInput | null = this.serachTemplateProperty(childProperties.properties, idName);
                if (item != null) {
                  return item;
                }
              }
            }
          } else {
            return null;
          }
        }
      }

      if (property.id == idName) {
        return property as ApplicationTemplateItemInput;
      }
    }
    return null;
  }

  /**
   * ### propertiesを持つ要素か否か
   */
  public hasProperties(item: ApplicationTemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.RADIO_GROUP: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  public initializeCheckboxAnswer(properties: TemplateItem[]): void {
    for (const item of properties) {
      switch (item.type) {
        case TemplateType.MULTIPLE_CHECKBOX: {
          const templateItemMultipleCheckbox: TemplateItemMultipleCheckbox = item as TemplateItemMultipleCheckbox;
          for (let i = 0; i < templateItemMultipleCheckbox.questions.length; i++) {
            templateItemMultipleCheckbox.answer[i] = templateItemMultipleCheckbox.answer[i] ? templateItemMultipleCheckbox.answer[i] : false;
          }
          break;
        }
        case TemplateType.SECTION: {
          const sectionItem: TemplateItemSection = item as TemplateItemSection;
          this.initializeCheckboxAnswer(sectionItem.properties);
          break;
        }
        case TemplateType.RADIO_GROUP: {
          const radioGroupItem: TemplateItemRadioGroup = item as TemplateItemRadioGroup;
          for (const childItem of radioGroupItem.properties) {
            if (childItem && Array.isArray(childItem) == true) {
              this.initializeCheckboxAnswer(childItem.properties);
            }
          }
          break;
        }
        case TemplateType.TEXT:
        case TemplateType.RADIO:
        default: {
          break;
        }
      }
    }

    return;
  }

  /**
   * ### idInSystemが同じ他施設の待機者を取得する
   */
  public async getWaitersInSystem(residentIdInSystem: number): Promise<Array<WaitersGetModel>> {
    const response: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, residentIdInSystem);
    return response;
  }

  /**
   * ### 閲覧可能施設を取得する
   *
   * - 自施設は含めない
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 他施設の待機者の判定結果を表示する際の選択肢を作成する
   */
  public createOtherFacilitiesJudgedOptions(waiterViewableFacilities: Array<FacilityGetModel>, waitersInSystem: Array<WaitersGetModel>): Array<{ value: string; text: string }> {
    const options: Array<{ value: string; text: string }> = [];

    options.push({
      text: "施設を選択してください",
      value: ""
    });

    for (const facility of waiterViewableFacilities) {
      for (const waiter of waitersInSystem) {
        if (waiter.facilityId == facility.id) {
          options.push({
            text: facility.name,
            value: `${facility.id}`
          });
        }
      }
    }

    return options;
  }

  /**
   * ### 同時に登録する施設選択肢を作成する
   */
  public createConcurrentRegisterFacilities(waiterViewableFacilities: Array<FacilityGetModel>, waitersInSystem: Array<WaitersGetModel>): Array<TargetFacility> {
    const targets: Array<TargetFacility> = [];

    let viewableFacilityMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    for (const facility of waiterViewableFacilities) {
      viewableFacilityMap.set(facility.id, facility);
    }

    for (const waiter of waitersInSystem) {
      if (waiter.status == ResidentStatus.RESIDENT) {
        continue;
      }

      const facility: FacilityGetModel | undefined = viewableFacilityMap.get(waiter.facilityId);
      targets.push({
        id: waiter.facilityId,
        name: facility ? facility.name : "",
        checked: false
      });
    }

    return targets;
  }

  /**
   * ### 自施設かどうか
   */
  get _isMineFacility(): boolean {
    const facilityId: number = Number(this.$route.params.facilityid);
    if (userStore.loginUser.facilityId == facilityId) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### 更新イベント
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 待機者の判定内容を更新する
   */
  public updateWaiter(): void {
    const requestBody: WaitersPatchModel = this.createRequestBody(
      this.sourceTemplate as AdmissionJudgementTemplatesGetModel,
      this.template as Template,
      this.judged,
      this.concurrentRegisterFacilities
    );
    this.$emit("onUpdate", requestBody);

    return;
  }

  /**
   * ### 待機者更新のリクエストボディを作成する
   */
  public createRequestBody(
    sourceTemplate: AdmissionJudgementTemplatesGetModel,
    template: Template,
    judged: boolean,
    concurrentRegisterFacilities: Array<TargetFacility>
  ): WaitersPatchModel {
    let waiter: WaitersGetModel = this.waiter as WaitersGetModel;

    let facilities: Array<number> = [];
    for (const facility of concurrentRegisterFacilities) {
      if (facility.checked == true) {
        facilities.push(facility.id);
      }
    }

    waiter.waiter.admissionJudgement = {
      templateId: sourceTemplate.id,
      information: sourceTemplate.information,
      templateAnswer: template,
      judged: judged,
      updated: 0,
      created: 0
    };

    const requestBody: WaitersPatchModel = {
      waiter: waiter.waiter,
      concurrentRegisterFacilities: facilities,
      updated: waiter.updated,
      updateType: UpdateType.WAITER_JUDGEMENT_APPLICATION
    };

    return requestBody;
  }

  /**
   * ### 待機者の他施設のでの入居判定結果を表示する
   */
  public onOtherFacilitiesJudged(): void {
    this.showOtherFacilitiesJudgedModal = true;
    return;
  }
}
</script>

<style lang="scss" scoped>
.judgement-waiter-tab {
  position: relative;

  .switch {
    width: 70px;
  }
  .point {
    font-weight: bold;
    font-size: 20px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;

    .update-btn {
      width: 120px;
    }
  }
}
</style>
